import { type IMapLayerSource, MapLayerType } from '../layers/MapLayer.ts';
import { fetchArcGISLayerMetadata } from './fetchArcGISLayerMetadata.ts';
import { fetchWfsLayerMetadata } from './fetchWfsLayerMetadata.ts';
import { type LayerMetadataResult } from './LayerMetadata.ts';

/**
 * Resolver di introspezione di un protocollo. Ogni protocollo dichiara
 * quando la config è sufficiente, quali opzioni concorrono alla cache
 * e come recuperare i metadati.
 */
interface ILayerMetadataResolver {
  /** True quando la config contiene ciò che serve al protocollo */
  isConfigured(layer: IMapLayerSource): boolean;
  /** Porzione protocollo-specifica della chiave di cache */
  cacheKey(layer: IMapLayerSource): unknown[];
  resolve(layer: IMapLayerSource): Promise<LayerMetadataResult>;
}

/**
 * Resolver di introspezione per tipo di layer. Per supportare un nuovo
 * tipo di servizio (es. WMS GetCapabilities) è sufficiente aggiungere
 * una entry.
 */
const metadataResolvers: Partial<Record<MapLayerType, ILayerMetadataResolver>> =
  {
    [MapLayerType.ArcGISRestVector]: {
      isConfigured: () => true,
      cacheKey: () => [],
      resolve: layer => fetchArcGISLayerMetadata(layer.url ?? ''),
    },
    [MapLayerType.WFS]: {
      isConfigured: layer => !!layer.wfs?.typeName,
      cacheKey: layer => [layer.wfs?.typeName, layer.wfs?.version],
      resolve: layer => fetchWfsLayerMetadata(layer.url ?? '', layer.wfs),
    },
  };

export function isMetadataSupportedLayerType(
  type: MapLayerType | undefined,
): boolean {
  return type !== undefined && metadataResolvers[type] !== undefined;
}

/**
 * True se la configurazione contiene le opzioni protocollo-specifiche
 * necessarie all'introspezione (es. il typeName per un layer WFS).
 */
export function isLayerMetadataConfigured(layer: IMapLayerSource): boolean {
  const resolver =
    layer.type !== undefined ? metadataResolvers[layer.type] : undefined;
  return resolver ? resolver.isConfigured(layer) : false;
}

/**
 * Chiave di cache dell'introspezione: tipo, URL e le opzioni che il
 * protocollo dichiara rilevanti.
 */
export function getLayerMetadataCacheKey(layer: IMapLayerSource): unknown[] {
  const resolver =
    layer.type !== undefined ? metadataResolvers[layer.type] : undefined;
  return [
    'map-layer-metadata',
    layer.type,
    layer.url,
    ...(resolver?.cacheKey(layer) ?? []),
  ];
}

/**
 * True se l'URL è un http(s) assoluto su cui ha senso tentare l'introspezione.
 */
export function isIntrospectableLayerUrl(url: string | undefined): boolean {
  if (!url) return false;

  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Introspezione dei metadati di un layer in base al tipo di servizio.
 * Non lancia mai: gli errori sono rappresentati nel `LayerMetadataResult`.
 */
export async function fetchLayerMetadata(
  layer: IMapLayerSource,
): Promise<LayerMetadataResult> {
  const resolver =
    layer.type !== undefined ? metadataResolvers[layer.type] : undefined;
  if (!resolver) return { status: 'unsupported-type' };

  return resolver.resolve(layer);
}
